import * as React from 'react';
import cx from 'classnames';
import {
  isArray,
  map,
  reduce,
} from 'lodash';

import { LazyImage } from 'src/widgets/Image/LazyImage';
import { Tooltip } from 'src/widgets/Tooltip/Tooltip';

import {
  ICardTabConfig,
  Card,
} from './Card';
import { Detail } from './Detail';
import { List } from './ListCard';
import { useSocialProfileContext } from '../hooks/useSocialProfileContext';
import {
  DetailConfigType,
  getDetailConfigs,
} from '../utils';

const { useMemo, useRef } = React;
import styles from './SummaryCard.scss';

const BRAND_LIMIT = 15;

export const SummaryCard: React.FC<{ className?: string }> = (props) => {
  const { className } = props;
  const {
    hasData,
    network,
  } = useSocialProfileContext();
  const audienceTabRef = useRef<HTMLDivElement>(null);
  const creatorTabRef = useRef<HTMLDivElement>(null);

  const tabs: ICardTabConfig[] = (() => {
    switch (network) {
      case 'blog':
      case 'facebook':
      case 'pinterest':
      case 'tiktok':
      case 'twitter':
        return [
          {
            key: 'AUDIENCE',
            header: 'Summary',
            contentRef: audienceTabRef,
            content: <AudienceSummary />,
            tabClassName: styles.audienceTab,
          },
        ];
      case 'instagram':
        return [
          {
            key: 'AUDIENCE',
            header: 'Audience Summary',
            tabClassName: styles.audienceTab,
            contentRef: audienceTabRef,
            content: <AudienceSummary />,
          },
          {
            key: 'CREATOR',
            header: 'Creator Summary',
            tabClassName: styles.creatorTab,
            contentRef: creatorTabRef,
            content: <CreatorSummary />,
          },
        ];
    }
    return [];
  })();

  return (
    <Card
      className={cx(className, styles.SummaryCard, styles[network])}
      header={hasData ? `${tabs[0].header}` : ''}
      tabConfigs={hasData ? tabs : []}
    />
  );
};

const AudienceSummary: React.FC = () => {
  const {
    hasData,
    network,
    socialAccount,
    fetchingDetailForId
  } = useSocialProfileContext();

  const isLoading = !!fetchingDetailForId;

  // Set detail order and values
  const detailConfigs = useMemo(() => {
    if (!hasData) {
      return [];
    }
    switch (network) {
      case 'blog':
        return getDetailConfigs({
          detailTypes: [
            DetailConfigType.Followers,
            DetailConfigType.BounceRate,
            DetailConfigType.PageViewsPerVisit,
            DetailConfigType.SecondsOnSite,
          ],
          socialAccount,
          styles,
          isLoading
        });
      case 'facebook':
        return getDetailConfigs({
          detailTypes: [
            DetailConfigType.Likes,
            DetailConfigType.FollowerBasedEngagementRate,
            DetailConfigType.AverageEngagementPerPost,
          ],
          socialAccount,
          styles,
          isLoading
        });
      case 'instagram':
        return getDetailConfigs({
          detailTypes: [
            DetailConfigType.AverageEngagementPerPost,
            DetailConfigType.ImpressionPerStory,
            DetailConfigType.AudienceAuthenticity,
            DetailConfigType.Gender,
            DetailConfigType.TopCountry,
            DetailConfigType.AgeRange,
            DetailConfigType.TopState,
            DetailConfigType.Interests,
          ],
          socialAccount,
          styles,
          isLoading
        });
      case 'pinterest':
        return getDetailConfigs({
          detailTypes: [
            DetailConfigType.AverageEngagementPerPost,
            DetailConfigType.ViewBasedEngagementRate,
            DetailConfigType.OriginalContentRatio,
            DetailConfigType.Followers,
          ],
          socialAccount,
          styles,
          isLoading
        });
      case 'tiktok':
        return getDetailConfigs({
          detailTypes: [
            DetailConfigType.TypicalViewsPerPost,
            DetailConfigType.LikesPerPost,
            DetailConfigType.AverageViewsPerPost,
            DetailConfigType.CommentsPerPost,
            DetailConfigType.ViralPostRatio,
            DetailConfigType.ViewBasedEngagementRate,
          ],
          socialAccount,
          styles,
          isLoading
        });
      case 'twitter':
        return getDetailConfigs({
          detailTypes: [
            DetailConfigType.AverageEngagementPerPost,
            DetailConfigType.FollowerBasedEngagementRate,
          ],
          socialAccount,
          styles,
          isLoading
        });
    }
  }, [hasData, network, socialAccount]);

  return (
    <div className={styles.detailGrid}>
      {map(detailConfigs, (data, i) => (
        <Detail
          data={data}
          key={`audience-summary-data-${i}`}
        />
      ))}
    </div>
  );
};

const CreatorSummary: React.FC = () => {
  const {
    hasData,
    socialAccount,
  } = useSocialProfileContext();

  // Interests
  const interests = hasData && isArray(socialAccount.interests)
    ? socialAccount.interests.slice(0, 4)
    : [];
  const hasInterests = interests.length > 0;
  const renderInterests = () => (
    <div className={styles.interestsWrapper}>
      <List
        flow='vertical'
        items={interests}
      />
    </div>
  );

  // Top brands
  const topBrands = hasData && socialAccount.report && isArray(socialAccount.report.top_brands)
    ? socialAccount.report.top_brands
    : [];
  const hasTopBrands = topBrands.length > 0;
  const renderBrandGrid = () => {
    const brandTooltipsRef: { [name: string]: React.RefObject<HTMLImageElement> } = (
      reduce(topBrands.slice(0, BRAND_LIMIT), (refs, brand) => {
        refs[brand.username] = React.createRef<HTMLImageElement>();
        return refs;
      }, {})
    );
    return (
      <div className={styles.brandGrid}>
        {topBrands.slice(0, BRAND_LIMIT).map((brand, i) => (
          <React.Fragment key={`brand-${i}`}>
            <LazyImage
              className={styles.brand}
              src={brand.logo_url}
              ref={brandTooltipsRef[brand.username]}
            />
            <Tooltip
              placement='bottom'
              mountRef={brandTooltipsRef[brand.username]}
              autoRegisterListener={true}
              tooltipColor='black'
            >
              {brand.name}
            </Tooltip>
          </React.Fragment>
        ))}
        {topBrands.length > BRAND_LIMIT && (
          <div className={styles.moreBrands}>
            +{topBrands.length - BRAND_LIMIT}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.creatorSummary}>
      <div
        className={cx(styles.interests, {
          [styles.noData]: !hasInterests,
        })}
      >
        <h5>Interests</h5>
        {hasInterests && renderInterests()}
      </div>
      <div
        className={cx(styles.brands, {
          [styles.noData]: !hasTopBrands,
        })}
      >
        <h5>Brand Mentions</h5>
        {hasTopBrands && renderBrandGrid()}
      </div>
    </div>
  );
};
